<template>
  <div class="relative">
    <div
      id="chart-canvas"
      class="align-center relative flex min-h-[70vh] flex-1 flex-col pt-5"
    >
      <ChartTabHeader
        class="z-adjustment-20"
        :title="title"
        :end-date="endDate"
        :raw-data="rawData"
        :category="category"
        :min-date="MIN_DATE"
        :start-date="startDate"
        :authorized="authorized"
        :show-overlay="showOverlay"
        :data-for-download="dataForDownload"
        :disable-download-button="!showGraph || loading"
        @update-selected-dates="updateSelectedDates"
      >
        <template #extra-actions>
          <Dropdown
            v-model="selectedCurrency"
            class="mb-5"
            :options="['CNY', 'USD']"
            @change="onCurrencyChange"
          />
        </template>
      </ChartTabHeader>

      <div
        v-if="loading"
        class="absolute left-0 top-44 w-full lg:top-72"
        data-testid="chart-page-loader"
      >
        <TheLoader />
      </div>

      <div v-else class="flex flex-col">
        <!-- chart -->

        <div
          v-if="showGraph"
          class="relative flex flex-1 items-stretch justify-center"
        >
          <SubscriptionOverlay :show-overlay="showOverlay" />
          <div class="flex flex-1 items-stretch justify-center">
            <ChartContainer
              class="z-10"
              :title="title"
              :hashes="hashes"
              :raw-data="rawData"
              :categories-order="categories"
              :replace-legend="replaceLegend"
              :market="category"
              @set-dataset-visibility="datasetVisibilityListener"
            />
          </div>
          <BenchmarkWatermark top-class="top-52" />
        </div>

        <!-- messages -->
        <div v-else class="flex flex-1 items-center justify-center">
          <div class="flex flex-col items-center justify-center px-6">
            <IconChart class="mb-6 h-36 w-32 object-contain" />
            <div class="mb-3 flex">
              <IconGreetingPerson
                class="mb-1 h-6 w-6 object-scale-down"
                src="~assets/icons/GreetingPerson.svg"
              />
              <div class="font-semibold">Almost there!</div>
            </div>
            <div v-for="message in messages" :key="message" class="text-sm">
              {{ messages.length > 1 ? '-' : '' }} {{ message }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <AssessmentsModal
      v-if="authorized && catalog.length > 0 && isNotDesktop"
      v-model:selected-currency="selectedCurrency"
      :catalog="catalog"
      :combinations="combinations"
      :authorized="authorized"
      :mineral="category"
      :min-date="MIN_DATE"
      :end-date="endDate"
      :start-date="startDate"
      :time-line-indicator="timeLineIndicator"
      @apply-filters="applyFilters"
      @update-selected-dates="updateSelectedDates"
    />
  </div>
</template>

<script setup lang="ts">
import type { TCommon, TGeneral, TRecord } from '@/src/types/common'
import { sub } from 'date-fns'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'

import useAuthentication from '~/composables/useAuthentication'
import useChart from '~/stores/charts'
import { useChartSidebarFilterStore } from '~/stores/chartSidebarFilter'
import { useModalStore } from '~/stores/modal'
import { isLithiumIonBatteries } from '~/src/utils/download-xlsx-config'
import { useEventBus } from '~/composables/useEventBus'
import { useAuthorization } from '~/composables/useAuthorization'
import { logError } from '~/utils/log-error'

import TheLoader from '~/components/TheLoader.vue'
import AssessmentsModal from '~/components/assessments/Modal.vue'
import ChartContainer from '~/components/shared/price-assessments/ChartContainer.vue'
import SubscriptionOverlay from '~/components/price-assessments/SubscriptionOverlay.vue'
import BenchmarkWatermark from '~/components/BenchmarkWatermark.vue'

import IconChart from '~/assets/icons/Chart.svg'
import IconGreetingPerson from '~/assets/icons/GreetingPerson.svg'

import {
  BLACK_MASS,
  LITHIUM,
  RARE_EARTHS,
  SYNTHETIC_GRAPHITE,
} from '~/constants/services/constants'
import {
  CATEGORIES_PRICES,
  FILTERS_CHART_NAME,
  SHIPPING_ROUTES_PRICES,
} from '~/constants/chart'
import type { RSFilterType } from '~/types/rudderstack.type'
import type { TProduct, TProductData } from '~/src/types/product'
import { useChartTab } from '~/composables/useChartTab'
import cloneDeep from 'lodash/cloneDeep'
import ChartTabHeader from '~/components/price-assessments/ChartTabHeader.vue'
import Dropdown from '~/components/shared/common/Dropdown.vue'

const MIN_DATE = new Date(2016, 0, 29)

const props = defineProps<{
  product: TProduct
  productData: TProductData
  subscription: string
}>()

const { $rudderstack } = useNuxtApp()
const { isNotDesktop } = useScreenSize()

const useChartStore = useChart()
const { setHashes } = useChartStore
const { getData, fetchCatalog } = useChartTab()
const { close: closeModal } = useModalStore()
const { hasMembership } = useAuthorization()
const { isAuthenticated: checkAuthentication, authenticationChanged } =
  useAuthentication()
const { on, off } = useEventBus()
const chartSidebarFilterStore = useChartSidebarFilterStore()

const timeLineIndicator = ref('Last Calendar Year')
const startDate = ref(sub(new Date(), { years: 1 }).toISOString())
const endDate = ref(new Date().toISOString())
const filterType = ref<RSFilterType>('price_data_filter')
const loading = ref(true)
const firstLoad = ref(true)
const chartOptions = ref<TRecord>({})
const rawData = ref<TCommon>(null)
const dataForDownload = ref<TCommon>(null)
const previousStartDate = ref(sub(new Date(), { years: 1 }).toISOString())
const previousEndDate = ref(new Date().toISOString())
const authorized = ref()
const showOverlay = ref(false)
const messages = ref([
  'You need to select a Category from the filters',
  'You need to select a Shipping Route from the filters',
])
const selectedCurrency = ref('USD')

const category = computed(() => props.product.category)
const requiredFilters = computed(() => props.productData.requiredFilters)
const title = computed(() => `${props.product.name} Prices`)
const hashes = computed(() => useChartStore.hashes)
const isLithium = computed(() => category.value === LITHIUM.category)
const isRareEarths = computed(() => category.value === RARE_EARTHS.category)
const isBlackMass = computed(() => category.value === BLACK_MASS.category)

const showGraph = computed(
  () =>
    optionsReady.value &&
    validateDatasets() &&
    (messages.value.length === 0 || isValidGlobalWeightedAverage.value) &&
    loading.value === false,
)

const catalog = computed<TGeneral[]>(() =>
  chartSidebarFilterStore
    .getPricesCatalogByMineral(props.product.category)
    .sort((a, b) => a.level - b.level),
)

const combinations = computed(() => {
  return chartSidebarFilterStore.getAvailableCombinations(category.value)
})

const isSyntheticGraphite = computed(
  () => category.value === SYNTHETIC_GRAPHITE.category,
)

const replaceLegend = computed(
  () => isBlackMass.value || isSyntheticGraphite.value,
)

const optionsReady = computed(() => {
  if (isValidGlobalWeightedAverage.value || !authorized.value) {
    return !firstLoad.value
  }

  let options = Object.keys(chartOptions.value).filter(
    (_option) => chartOptions.value[_option]?.length,
  )

  if (isLithium.value) {
    options = options.filter((item) => item !== 'shippingRoutes')
  }

  if (requiredFilters.value.length !== options.length) {
    return false
  }

  const isOptionCheckedArray = options.map((_option) => {
    return !isEmpty(chartOptions.value[_option])
  })

  if (!isEmpty(isOptionCheckedArray)) {
    return isOptionCheckedArray.every((x) => x) && !firstLoad.value
  }

  return !firstLoad.value
})

const categories = computed(() => {
  if (catalog.value.length === 0) return []

  const categories = catalog.value.find((item) => item.name === 'categories')

  if (!categories) return []

  return categories.options.map(({ name }: { name: string }) => name)
})

const isGlobalWeightedAverage = computed(() => {
  return chartOptions.value.categories?.some(
    (_option: TCommon) =>
      (_option.data.name ===
        SHIPPING_ROUTES_PRICES.global_weighted_average.name ||
        _option.data.name === CATEGORIES_PRICES.china_weighted_index.name ||
        CATEGORIES_PRICES.china_weighted_average) &&
      _option.checked,
  )
})

const isValidGlobalWeightedAverage = computed(() => {
  return (
    (isLithiumIonBatteries(category.value) || isRareEarths.value) &&
    isGlobalWeightedAverage.value &&
    chartOptions.value.categories?.length === 1
  )
})

const updateSelectedDates = (dateRange: {
  range: { start: Date; end: Date }
  option: string
}) => {
  if (!dateRange) return

  startDate.value = dateRange.range.start.toISOString()
  endDate.value = dateRange.range.end.toISOString()
  timeLineIndicator.value = dateRange.option
}

const trackChartInteraction = () => {
  $rudderstack.trackChartInteraction({
    chart_title: title.value,
    chart_type: 'lines',
    start_date: startDate.value,
    end_date: endDate.value,
    current_filters: chartOptions.value,
  })
}

const trackFilterApplied = () => {
  $rudderstack.trackFilterApplied({
    chart_title: title.value,
    filter_type: filterType.value,
    filter_values: {
      price_data_filters: chartOptions.value,
      date_range_filters: {
        start_date: startDate.value,
        end_date: endDate.value,
      },
    },
  })
}

const datasetVisibilityListener = ({ hashes }: { hashes: string[] }) => {
  if (!rawData.value) return

  const _dataForDownload: TRecord = {}

  Object.keys(rawData.value).forEach((key) => {
    if (!_dataForDownload[key]) {
      _dataForDownload[key] = []
    }

    const datasetsChildren: TCommon[] = []

    hashes?.forEach((hash) => {
      const datasets = rawData.value[key]?.data?.find(
        (dataset: TCommon) => dataset.hash === hash,
      )
      if (datasets) {
        datasetsChildren.push(datasets)
      }
    })

    if (datasetsChildren.length > 0) {
      _dataForDownload[key] = {
        ...rawData.value[key],
        data: datasetsChildren,
      }
    }
  })
  dataForDownload.value = _dataForDownload
  setHashes(hashes)
}

const applyFilters = async (options: TRecord) => {
  await fetchData()
  chartOptions.value = options
  previousStartDate.value = startDate.value
  previousEndDate.value = endDate.value
  fetchData()
  closeModal('priceAssessmentFilters')
}

const debouncedFetch = debounce(() => {
  if (isNotDesktop.value) return
  fetchData()
}, 1000)

const checkAuthorization = async () => {
  const membership = await hasMembership(props.subscription)
  authorized.value = membership && checkAuthentication()
}

const fetchData = async () => {
  await checkAuthorization()
  loading.value = true
  rawData.value = null

  try {
    const {
      data,
      dataForDownload: _dataForDownload,
      showOverlay: _showOverlay,
    } = await getData({
      category: category.value,
      currency: selectedCurrency.value,
      startDate: startDate.value,
      endDate: endDate.value,
      chartOptions: chartOptions.value,
      hashes: hashes.value,
      authorized: authorized.value,
    })

    rawData.value = data
    dataForDownload.value = _dataForDownload
    showOverlay.value = _showOverlay
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }

  if (firstLoad.value) {
    trackFilterApplied()
  }

  firstLoad.value = false
}

const updateMessage = (newMessages: string[]) => {
  messages.value = newMessages
}

const validateDatasets = () => {
  let ready = false
  messages.value = []
  if (rawData.value) {
    const datasetsToCheck = ['y1', 'y2']

    ready = datasetsToCheck.some((key) => {
      const dataset = rawData.value[key]?.data
      return dataset && dataset.length > 0
    })
  }
  if (!ready && optionsReady.value && !loading.value) {
    updateMessage([
      `Unfortunately the selection you have
      made has returned no results. Try a
      different combination.`,
    ])
  }
  return ready
}

watch(chartOptions, () => {
  if (chartOptions.value.categories?.length) return

  messages.value = Object.entries(chartOptions.value)
    .filter(
      ([key, value]) =>
        isEmpty(value) && props.productData.requiredFilters.includes(key),
    )
    .map(
      ([key, _]) =>
        `You need to select a ${FILTERS_CHART_NAME[key]} from the filters`,
    )
})

watch(authenticationChanged, async (newValue, oldValue) => {
  if (!!newValue === !!oldValue) return
  if (newValue) {
    await checkAuthorization()
    await Promise.all([
      fetchCatalog(category.value, authorized.value),
      fetchData(),
    ])
    getInitialOptions()
  }
})

watch(chartOptions, (value, old: unknown) => {
  if (!firstLoad.value && optionsReady.value && value.value !== old.value) {
    filterType.value = 'price_data_filter'

    debouncedFetch()
  }
})

watch([startDate, endDate], () => {
  if (!firstLoad.value && optionsReady.value) {
    filterType.value = 'date_range_filter'

    debouncedFetch()
  }
})

const getInitialOptions = () => {
  const filters = cloneDeep(catalog.value)
  chartOptions.value = getSelectedOptions(filters)
  previousStartDate.value = startDate.value
  previousEndDate.value = endDate.value
}

const onCurrencyChange = async () => {
  applyFilters(chartOptions.value)
}

onMounted(async () => {
  await checkAuthorization()

  await Promise.all([
    fetchCatalog(category.value, authorized.value),
    fetchData(),
  ])
  getInitialOptions()

  on('setDatasetVisibility', datasetVisibilityListener)
  on('on-legend-click', trackFilterApplied)
  on('on-category-click', trackFilterApplied)
  on('on-chart-hover', trackChartInteraction)

  if (isNotDesktop.value) return

  const section = document.getElementById('chart-canvas')

  if (!section) return

  const offset = 150
  const topPosition =
    section.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: topPosition, behavior: 'smooth' })
})

onBeforeMount(async () => {
  await checkAuthorization()
})

onBeforeUnmount(() => {
  setHashes([])
  off('setDatasetVisibility', datasetVisibilityListener)
  off('on-legend-click', trackFilterApplied)
  off('on-category-click', trackFilterApplied)
  off('on-chart-hover', trackChartInteraction)
})
</script>
